import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
type ScreenId = 'home' | 'settle' | 'history';
const screens: {
  id: ScreenId;
  label: string;
  url: string;
  alt: string;
}[] = [{
  id: 'home',
  label: 'Home',
  url: 'https://cdn.magicpatterns.com/uploads/xqPSzmtVfdsEoSWhQ3mvuc/WhatsApp_Image_2026-08-26_at_20.13.12.jpg',
  alt: 'KittyPot home screen for a Pub kitty, showing total spent, the number of people sharing it, and controls to add an expense or receipt'
}, {
  id: 'settle',
  label: 'Settle',
  url: 'https://cdn.magicpatterns.com/uploads/uWNBZNF7MRi2yCCWeewHhk/WhatsApp_Image_2026-08-13_at_19.06.02.jpg',
  alt: 'KittyPot settlement screen showing what you are owed and who needs to pay whom'
}, {
  id: 'history',
  label: 'History',
  url: 'https://cdn.magicpatterns.com/uploads/mxhgmAEJufMAexcc2t4eXp/WhatsApp_Image_2026-08-13_at_19.06.02_(2).jpg',
  alt: 'KittyPot history screen listing recorded shared expenses with who paid and how much'
}];
const features = ['Everyone sees who owes what', 'Fair splits, equal or custom', 'A simple way to settle up', 'Settle up your own way', 'Only kitty members see its expenses'];
export function ValueProposition() {
  const [activeScreen, setActiveScreen] = useState<ScreenId>('home');
  const prefersReducedMotion = useReducedMotion();
  const activeIndex = screens.findIndex(screen => screen.id === activeScreen);
  return <section className="py-20 lg:py-28 bg-kitty-yellow-50/50 overflow-hidden" data-id="element-307" data-mp="35zfisiv47o8x">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-308" data-mp="30xq48t8oosg3">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-309" data-mp="3ey9b3x1exsgo">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-310" data-mp="39ywq7hu3qvgq">
            <p className="text-sm font-semibold text-kitty-yellow-600 uppercase tracking-widest mb-3" data-id="element-311" data-mp="1flyj173p8exm">
              Full transparency
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-kitty-dark mb-6 leading-tight tracking-tight" data-id="element-312" data-mp="3fphk2zz2kpdp">
              Keep every shared expense clear
            </h2>
            <p className="text-lg text-gray-500 mb-8 max-w-md" data-id="element-313" data-mp="3pnf7jl93ui59">
              KittyPot brings every expense, balance and settlement update into
              one shared view, so your group always knows what is owed, pending
              and settled.
            </p>

            <ul className="space-y-4" data-id="element-314" data-mp="7eekvjc7lmn2">
              {features.map((feature, index) => <motion.li key={feature} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2 + index * 0.08
            }} className="flex items-center gap-3" data-id="element-315" data-mp="c5wetnftemyj" data-mpi={index}>
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-kitty-yellow/20 flex items-center justify-center" data-id="element-316" data-mp="q6gy0kpn6ksk">
                    <CheckCircle2 className="w-4 h-4 text-kitty-yellow-700" data-id="element-317" data-mp="25p4znroa08fn" />
                  </div>
                  <span className="text-gray-800 font-medium text-base" data-id="element-318" data-mp="21njt0o2c4qdw">
                    {feature}
                  </span>
                </motion.li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-319" data-mp="2tepwneh9l67x">
            <div className="relative z-10 mx-auto max-w-[390px]" data-id="element-320" data-mp="2kkkzkdbjeyjy">
              <div className="relative overflow-hidden rounded-[2.25rem] border-[5px] border-kitty-dark bg-white shadow-[0_24px_52px_-24px_rgba(27,28,30,0.45)]" data-id="element-321" data-mp="2fm9oy0df4k0o">
                <motion.div className="flex w-[300%]" animate={{
                x: `-${activeIndex * 100 / screens.length}%`
              }} transition={prefersReducedMotion ? {
                duration: 0
              } : {
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-322" data-mp="38c06yki68622">
                  {screens.map(screen => <img key={screen.id} src={screen.url} alt={screen.alt} aria-hidden={activeScreen !== screen.id} className="block h-auto w-1/3 flex-shrink-0" data-id="element-323" data-mp="2iwugf8crnaxt" />)}
                </motion.div>

                {/* Tap targets over the app's bottom navigation */}
                <div role="tablist" aria-label="KittyPot app screens" className="absolute bottom-0 left-0 flex w-full h-[6.4%]" data-id="element-324" data-mp="3fm20ebxqlbzm">
                  {screens.map(screen => <button key={screen.id} type="button" role="tab" aria-selected={activeScreen === screen.id} aria-label={`Show ${screen.label} screen`} onClick={() => setActiveScreen(screen.id)} className="flex-1 cursor-pointer rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-325" data-mp="31u5ewqqkq0c7" />)}
                </div>
              </div>

              <p className="mt-3 text-center text-xs font-medium text-gray-500" data-id="element-326" data-mp="ciylwaiqobq7">
                The KittyPot app — tap Home, Settle or History to explore.
              </p>
            </div>

            {/* Decorative blobs */}
            <div className="absolute top-10 -right-10 w-32 h-32 bg-kitty-yellow-200 rounded-full mix-blend-multiply filter blur-2xl opacity-60" data-id="element-327" data-mp="m7yo02sjfn8m" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-orange-200 rounded-full mix-blend-multiply filter blur-2xl opacity-60" data-id="element-328" data-mp="hcjefn1kcdi0" />
          </motion.div>
        </div>
      </div>
    </section>;
}